import { SelectOption } from '@vchasno/ui-kit';

import {
  ActionExecutorType,
  DocumentActionType,
  RetrieveVacationTaskTemplateResponse,
  TaskDocumentAccessSettings,
  TaskDocumentType,
  VacationTaskTemplateDocumentActionRetrieveSerializer, VacationTaskTemplateDocumentCreateSerializer,
  VacationTaskTemplateDocumentRetrieveSerializer,
} from 'api/api';

import { isDynamicExecutorOption } from '../dynamicExecutor.utils';
import {
  SectionAction,
  VacationTaskTemplateState,
} from './vacationTaskTemplate.types';

export const formatTaskTemplateDate = (date?: Date | null) => {
  if (!date) return '';

  // TODO Не локалізовано
  const intlDate = new Intl.DateTimeFormat('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  return intlDate.format(date);
};

export const buildInitialVacationTaskTemplateState =
  (): VacationTaskTemplateState => ({
    managerId: null,
    type: null,
    id: null,
    section: 'documents',
    name: '',
    activeDocument: TaskDocumentType.Application,
    documents: {
      [TaskDocumentType.Request]: {
        isActive: false,
        actions: [],
        fileDocumentId: null,
      },
      [TaskDocumentType.Application]: {
        isActive: false,
        isParallel: false,
        actions: [],
        fileDocumentId: null,
      },
      [TaskDocumentType.Order]: {
        isActive: false,
        isParallel: false,
        actions: [],
        fileDocumentId: null,
      },
    },
  });

const parseDocumentActions = (
  actions: VacationTaskTemplateDocumentActionRetrieveSerializer[],
): SectionAction[] =>
  actions.map((action, index) => ({
    type: action.action_type,
    executorType: action.executor_type,
    executorId: action.executor_id,
    order: index,
    id: Math.random(),
  }));

const findDocument = (
  type: TaskDocumentType,
  documents: VacationTaskTemplateDocumentRetrieveSerializer[],
) => documents.find((doc) => doc.document_type === type);

export function parseVacationTaskTemplate(
  response: RetrieveVacationTaskTemplateResponse,
): VacationTaskTemplateState {
  const state: VacationTaskTemplateState = {
    ...buildInitialVacationTaskTemplateState(),
    id: response.detail.version_identifier,
    type: response.detail.type,
    name: response.detail.name,
    periodType: response.detail.period_type,
  };

  if (response.detail.default_manager_id) {
    state.managerId = response.detail.default_manager_id;
  }

  const requestDocument = findDocument(
    TaskDocumentType.Request,
    response.detail.documents,
  );

  if (requestDocument) {
    const actions = parseDocumentActions(requestDocument.actions);

    state.documents[TaskDocumentType.Request] = {
      actions,
      isActive: actions.length > 0,
      isParallel: requestDocument.is_parallel_action,
      fileDocumentId: requestDocument.file_document_id,
    };
  }

  const applicationDocument = findDocument(
    TaskDocumentType.Application,
    response.detail.documents,
  );

  if (applicationDocument) {
    const actions = parseDocumentActions(applicationDocument.actions);

    state.documents[TaskDocumentType.Application] = {
      actions,
      isActive: actions.length > 0,
      isParallel: applicationDocument.is_parallel_action,
      accessSettings: applicationDocument.access_settings,
      fileDocumentId: applicationDocument.file_document_id,
    };
  }

  const orderDocument = findDocument(
    TaskDocumentType.Order,
    response.detail.documents,
  );

  if (orderDocument) {
    const actions = parseDocumentActions(orderDocument.actions);

    state.documents[TaskDocumentType.Order] = {
      actions,
      isActive: actions.length > 0,
      isParallel: orderDocument.is_parallel_action,
      accessSettings: orderDocument.access_settings,
      fileDocumentId: orderDocument.file_document_id,
    };
  }

  return state;
}

export const buildDocumentActionState = (
  type: DocumentActionType,
  option: SelectOption,
) => {
  if (isDynamicExecutorOption(option)) {
    return {
      type,
      executorType: option.value,
      executorId: null,
      id: Math.random(),
    };
  }

  return {
    type,
    executorId: option.value,
    executorType: ActionExecutorType.Static,
    id: Math.random(),
  };
};

export const buildTaskTemplateOrderCardTheme = (
  executorType: ActionExecutorType,
) => {
  if (executorType === ActionExecutorType.Initiator) {
    return 'light';
  }

  return 'primary';
};

export const buildIndexHandler =
  (fn: (index: number) => void) => (index: number) => () =>
    fn(index);

export const toggleDocumentAccessSettings = (
  currentSettings?: Nullable<TaskDocumentAccessSettings>,
) => {
  if (currentSettings === TaskDocumentAccessSettings.Private) {
    return TaskDocumentAccessSettings.Extended;
  }

  return TaskDocumentAccessSettings.Private;
};

export function mergeDocumentsWithFileInfo(
  documents: VacationTaskTemplateDocumentCreateSerializer[],
  fullDocuments: VacationTaskTemplateDocumentCreateSerializer[],
): VacationTaskTemplateDocumentCreateSerializer[] {
  return documents.map((document) => {
    const existingDocument = fullDocuments.find(
      (el) => el.file_document_id === document.file_document_id,
    );

    if (existingDocument?.file_document_params) {
      return {
        ...document,
        file_document_params: existingDocument.file_document_params,
      };
    }

    return document;
  });
}